import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { CategorieNdf, Depense } from '../../types/ndf';
import { BoutonSecondaire, dateFr, Etiquette } from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';
import { VisionneuseJustificatif } from './VisionneuseJustificatif';
import { ApercuJustificatif } from './ApercuJustificatif';
import { useState } from 'react';

/**
 * Deux dépenses côte à côte, pour trancher un doublon.
 *
 * Un composant, deux usages : la page de comparaison du salarié et l'écran de
 * validation. Un doublon se décide de la même façon des deux côtés — en
 * regardant les deux pièces, pas en lisant deux totaux.
 *
 * Côte à côte sur ordinateur, l'une au-dessus de l'autre sur téléphone : à
 * 375 px de large, deux colonnes ne se lisent pas.
 */
export function ComparaisonDepenses({
  idA,
  idB,
  onFermer,
}: {
  idA: string;
  idB: string;
  /** Absent : on est sur une page, le retour se fait par l'historique. */
  onFermer?: () => void;
}) {
  const navigate = useNavigate();
  const [apercu, setApercu] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'depenses'],
    queryFn: () => ndfApi.depenses.liste(),
    retry: false,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
  });

  const depenses = data?.depenses ?? [];
  const categories = categoriesData?.categories ?? [];
  const gauche = useMemo(() => depenses.find((d) => d.id === idA), [depenses, idA]);
  const droite = useMemo(() => depenses.find((d) => d.id === idB), [depenses, idB]);

  const fermer = () => (onFermer ? onFermer() : navigate(-1));

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!gauche || !droite) {
    return (
      <div className="space-y-3">
        <MessageErreur
          erreur={
            new Error(
              'Une des deux dépenses n’est pas accessible. Elle a peut-être été supprimée, ou appartient à un autre collaborateur.',
            )
          }
        />
        <BoutonSecondaire type="button" onClick={fermer}>
          Retour
        </BoutonSecondaire>
      </div>
    );
  }

  // Ce qui DIFFÈRE mérite d'être vu : c'est la seule chose qui permet de
  // trancher entre un vrai doublon et deux dépenses légitimement jumelles.
  const differences = new Set<string>();
  if (gauche.date_depense !== droite.date_depense) differences.add('date');
  if (Math.abs(gauche.montant_ttc - droite.montant_ttc) > 0.005) differences.add('montant');
  if (gauche.categorie_id !== droite.categorie_id) differences.add('catégorie');
  if ((gauche.description ?? '') !== (droite.description ?? '')) differences.add('description');
  if ((gauche.commercant ?? '') !== (droite.commercant ?? '')) differences.add('commerçant');

  return (
    <div className="space-y-4 pb-4">
      {apercu && (
        <ApercuJustificatif
          url={ndfApi.depenses.urlJustificatif(apercu)}
          onFermer={() => setApercu(null)}
        />
      )}

      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-titre text-lg font-bold text-nuit">Comparer deux dépenses</h1>
          <p className="text-sm text-nuit/60">
            {differences.size === 0
              ? 'Ces deux dépenses sont identiques sur tous les champs comparés.'
              : `Ce qui diffère est surligné : ${[...differences].join(', ')}.`}
          </p>
        </div>
        <BoutonSecondaire type="button" onClick={fermer}>
          Retour
        </BoutonSecondaire>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <Colonne
          depense={gauche}
          categories={categories}
          differences={differences}
          navigable={!onFermer}
          onAgrandir={() => setApercu(gauche.id)}
        />
        <Colonne
          depense={droite}
          categories={categories}
          differences={differences}
          navigable={!onFermer}
          onAgrandir={() => setApercu(droite.id)}
        />
      </div>
    </div>
  );
}

function Colonne({
  depense,
  categories,
  differences,
  navigable,
  onAgrandir,
}: {
  depense: Depense;
  categories: CategorieNdf[];
  differences: Set<string>;
  /** En cours de validation, ouvrir une autre page ferait perdre le parcours. */
  navigable: boolean;
  onAgrandir: () => void;
}) {
  const navigate = useNavigate();
  const categorie = categories.find((c) => c.id === depense.categorie_id);
  const sousCategorie = categories.find((c) => c.id === depense.sous_categorie_id);

  return (
    <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
      <div>
        <h2 className="font-titre font-bold text-nuit">
          {categorie?.libelle ?? 'Dépense'}
          {sousCategorie ? ` · ${sousCategorie.libelle}` : ''}
        </h2>
        <p className="text-sm text-nuit/60">{dateFr(depense.date_depense)}</p>
      </div>

      <dl className="space-y-1 text-sm">
        <Ligne libelle="Date" valeur={dateFr(depense.date_depense)} differe={differences.has('date')} />
        <Ligne
          libelle="Montant TTC"
          valeur={depense.montant_ttc.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
          differe={differences.has('montant')}
        />
        <Ligne
          libelle="Catégorie"
          valeur={categorie?.libelle ?? '-'}
          differe={differences.has('catégorie')}
        />
        <Ligne
          libelle="Commerçant"
          valeur={depense.commercant ?? '-'}
          differe={differences.has('commerçant')}
        />
        <Ligne
          libelle="Description"
          valeur={depense.description ?? '-'}
          differe={differences.has('description')}
        />
        {depense.facturable && (
          <Ligne libelle="Client" valeur={depense.client_nom ?? 'À préciser'} differe={false} />
        )}
      </dl>

      <div className="flex flex-wrap gap-2">
        {navigable && (
          <BoutonSecondaire
            type="button"
            onClick={() => navigate(`/notes-de-frais/depense/${depense.id}`)}
          >
            Ouvrir cette dépense
          </BoutonSecondaire>
        )}
        {depense.statut === 'brouillon' && <Etiquette>Brouillon</Etiquette>}
      </div>

      {/* Le justificatif fait partie de la comparaison : c'est en voyant les
          deux tickets qu'on reconnaît le même. */}
      {depense.a_justificatif === false ? (
        <p className="rounded border border-nuit/10 bg-nuit/[0.02] p-3 text-sm text-nuit/60">
          Aucun justificatif attaché.
        </p>
      ) : (
        <div className="rounded border border-nuit/10 bg-nuit/[0.02] p-2">
          <VisionneuseJustificatif
            url={ndfApi.depenses.urlJustificatif(depense.id)}
            onOuvrir={onAgrandir}
          />
        </div>
      )}
    </section>
  );
}

function Ligne({
  libelle,
  valeur,
  differe,
}: {
  libelle: string;
  valeur: string;
  differe: boolean;
}) {
  return (
    <div className="flex justify-between gap-3 border-b border-nuit/5 pb-1 last:border-0">
      <dt className="text-nuit/60">{libelle}</dt>
      <dd
        className={`text-right font-medium ${
          differe ? 'rounded bg-jaune-fond/40 px-1 text-nuit' : 'text-nuit'
        }`}
      >
        {valeur}
      </dd>
    </div>
  );
}
